import React__default, { useState } from 'react';
import { useIsMobileDevice } from '@belong/common/useIsMobileDevice';
import { IconInfo } from '@belong/icons/dist/IconInfo';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { ConditionalWrap } from '../ConditionalWrap/ConditionalWrap.js';

/**
 * If you have this tooltip inside another actionable element
 * you may need to pass the children manually and prevent the event propagation, ie:
 * <button onClick={(e) => e.stopPropagation()} type="button" >
 *   <IconInfo width={20} />
 * </button>
 */
/**
 * Note for the mobile approach:
 * Since the Tooltip component is not responsive by design https://github.com/radix-ui/primitives/issues/955#issuecomment-960610209
 * we are using the Popover component for mobile devices and the Tooltip component for desktop devices.
 */
function Tooltip({
  children = /*#__PURE__*/React__default.createElement("button", {
    className: "cursor-pointer fill-navy",
    type: "button"
  }, /*#__PURE__*/React__default.createElement(IconInfo, {
    width: 20
  })),
  content,
  open: controlledOpen,
  defaultOpen,
  onOpenChange,
  contentProps,
  arrowProps = {
    className: 'fill-navy'
  },
  portal = false
}) {
  const isMobile = useIsMobileDevice();
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  const handleOpenChange = newOpenState => {
    setIsOpen(newOpenState);
    onOpenChange?.(newOpenState);
  };
  const handleTriggerClick = () => {
    handleOpenChange(!isOpen);
  };
  if (isMobile) {
    return /*#__PURE__*/React__default.createElement(PopoverPrimitive.Root, {
      open: controlledOpen ?? isOpen,
      onOpenChange: handleOpenChange
    }, /*#__PURE__*/React__default.createElement(PopoverPrimitive.Trigger, {
      asChild: true,
      onClick: handleTriggerClick
    }, children), /*#__PURE__*/React__default.createElement(ConditionalWrap, {
      condition: portal,
      wrapper: children => /*#__PURE__*/React__default.createElement(PopoverPrimitive.Portal, null, children)
    }, /*#__PURE__*/React__default.createElement(PopoverPrimitive.Content, {
      ...contentProps
    }, content, /*#__PURE__*/React__default.createElement(PopoverPrimitive.Arrow, {
      ...arrowProps
    }))));
  }
  return /*#__PURE__*/React__default.createElement(TooltipPrimitive.Root, {
    open: controlledOpen,
    defaultOpen: defaultOpen,
    onOpenChange: onOpenChange
  }, /*#__PURE__*/React__default.createElement(TooltipPrimitive.Trigger, {
    asChild: true
  }, children), /*#__PURE__*/React__default.createElement(ConditionalWrap, {
    condition: portal,
    wrapper: children => /*#__PURE__*/React__default.createElement(TooltipPrimitive.Portal, null, children)
  }, /*#__PURE__*/React__default.createElement(TooltipPrimitive.Content, {
    ...contentProps
  }, content, /*#__PURE__*/React__default.createElement(TooltipPrimitive.Arrow, {
    ...arrowProps
  }))));
}

export { Tooltip };
